/**
 * The structural render gate — `review-surface-integrity` design D1.
 *
 * Rows are stored with the query that produced them, and the render path shows them
 * only when that query is the one currently selected; otherwise it is the loading
 * state. A facet switch fails the equality check on the very first paint, because
 * the selected state changes synchronously inside the click handler while any fetch
 * it triggers can only resolve later — there is no window in between where the
 * previous facet's rows and the new facet's heading can appear on screen together.
 *
 * This is not "clear first." Clearing procedurally (`setRows(null)` at the top of a
 * loader) was measured to have a one-frame floor: `setCategory` commits and paints
 * synchronously inside the click handler, while the effect that runs the clearing
 * loader only fires *after* that paint, so frame zero still shows the old rows under
 * the new heading. A structural gate has no such ordering to get right, because
 * nothing has to run between the click and the next paint for the invariant to hold
 * — the active query already changed, so the last-committed result already fails the
 * equality check, before any effect gets a chance to run at all.
 *
 * The same equality check subsumes request-ordering races. A response is only
 * committed if the query it was fetched for is still the latest one requested
 * (checked via a ref, not the possibly-stale closure the async function started
 * with) — so a slow response for an abandoned query can neither paint its own rows
 * nor blank out a newer result that already arrived.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface GatedQuery<Rows> {
  /** The rows for the currently active query, or `null` while there is none to show
   * yet — either the first fetch hasn't resolved, or the active query has moved on
   * from whatever the last-committed result belongs to. Never rows for a query other
   * than the current one. */
  rows: Rows | null;
  error: string | null;
  /** Re-run the fetch for the current query — for a caller-triggered refresh (a
   * completed background job, a manual retry) rather than a query change. */
  reload: () => Promise<void>;
}

export function useGatedQuery<Query, Rows>(
  query: Query,
  fetcher: (query: Query) => Promise<Rows>,
): GatedQuery<Rows> {
  const key = JSON.stringify(query);

  const [committed, setCommitted] = useState<{ key: string; rows: Rows } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read via refs, not closure, inside the async function below: `load` is memoized
  // on `key` alone (see its dependency array) so that a facet switch invalidates the
  // previous fetch by *identity*, not merely by the value it happens to compare
  // against — the fetcher and the query it closes over must still be the freshest
  // ones even though the memoized callback itself is not recreated on every render.
  const latestKeyRef = useRef(key);
  latestKeyRef.current = key;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const queryRef = useRef(query);
  queryRef.current = query;

  const load = useCallback(async () => {
    const requestedKey = latestKeyRef.current;
    const requestedQuery = queryRef.current;
    setError(null);
    try {
      const rows = await fetcherRef.current(requestedQuery);
      // D1: a response is committed only if its query is still the active one — the
      // same check the render path applies, so a late-arriving stale response can
      // never overwrite a newer, already-painted result.
      if (latestKeyRef.current === requestedKey) setCommitted({ key: requestedKey, rows });
    } catch (cause) {
      if (latestKeyRef.current === requestedKey) {
        setError(cause instanceof Error ? cause.message : String(cause));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `key` only,
    // deliberately: `query`/`fetcher` are read through refs above so this callback's
    // identity (and therefore the effect that re-runs it) tracks exactly the
    // equality check the render path uses, not incidental object identity.
  }, [key]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = committed && committed.key === key ? committed.rows : null;
  return { rows, error, reload: load };
}
